import { Search } from '../../types';

export type PipelinePhase = 'running' | 'recrawling' | 'paused' | 'complete';

export interface PipelineSummary {
  phase: PipelinePhase;
  isComplete: boolean;
  /** Short text for the card chip. */
  chipLabel: string;
  /** Completed vs total stages, for a mini progress meter. */
  progress: { done: number; total: number };
}

/**
 * Derive the card-level summary from a search's backend pipeline snapshot.
 *
 * Returns null when there is no pipeline to show (older payloads / backend not
 * yet deployed) so callers render nothing.
 *
 * `status` is orthogonal to `pipeline.currentStage`: the first run keeps
 * status 'pending' until the very end, the daily re-crawl resets the stages and
 * re-climbs while status stays 'active', and a paused search FREEZES its stages
 * (it is not refreshing) while status is 'paused'. So we map status explicitly.
 *
 * Phase logic:
 *  - complete:    every stage is done (backend marks all done at a terminal stage).
 *  - paused:      not complete AND status 'paused' — progress is frozen, not live;
 *                 callers should suppress any "in motion" affordance for it.
 *  - recrawling:  not complete AND status 'active' — the daily re-crawl reset the
 *                 stages and is re-climbing them; render this quietly.
 *  - running:     not complete, first run still in progress (status 'pending').
 */
export const summarizePipeline = (search: Search): PipelineSummary | null => {
  const stages = search.pipeline?.stages;
  if (!stages || stages.length === 0) return null;

  const done = stages.filter((s) => s.state === 'done').length;
  const isComplete = done === stages.length;

  let phase: PipelinePhase;
  if (isComplete) phase = 'complete';
  else if (search.status === 'paused') phase = 'paused';
  else if (search.status === 'active') phase = 'recrawling';
  else phase = 'running'; // status 'pending' = first run in progress

  const activeStage = stages.find((s) => s.state === 'active');

  let chipLabel: string;
  if (phase === 'complete') chipLabel = 'Ready';
  else if (phase === 'paused') chipLabel = activeStage?.label ?? 'Paused';
  // Same words as SearchProgressDrawer's recrawl note — one event, two views.
  // Not "Refreshing results": a recrawl reveals nothing on completion now, and
  // five pack cards saying it at once implied five parallel result streams
  // when the whole account shares one daily budget.
  else if (phase === 'recrawling') chipLabel = 'Checking for new roles';
  else chipLabel = activeStage?.label ?? 'Queued';

  return { phase, isComplete, chipLabel, progress: { done, total: stages.length } };
};
